import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import styles from "./UnlinkMeterModal.module.scss";
import Dropdown, { DropdownOption } from "../../../component/dropdown/Dropdown";
import Button from "../../../../../common/components/xepp/button/Button";
import { useGetCustomerDetailsForUtilityAdminQuery } from "../../../../../service/customer-service";
import { useUnlinkMeterByUtilityAdminMutation } from "../../../../../service/vending-service";
import { getErrorMessage } from "../../../../../utils/error-handler";

const UNLINK_REASONS: DropdownOption[] = [
    { value: "1", label: "Tenant Moving Out" },
    { value: "2", label: "Meter Replacement (Faulty)" },
    { value: "3", label: "Demolition / Disconnection" },
    { value: "4", label: "Data Correction" },
    { value: "5", label: "Customer Request" },
];

interface UnlinkMeterModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerId?: string;
}

const UnlinkMeterModal = ({ isOpen, onClose, customerId }: UnlinkMeterModalProps) => {
    const [selectedMeterId, setSelectedMeterId] = useState<string | null>(null);
    const [selectedReason, setSelectedReason] = useState<string | null>(null);

    const { data: customer, isLoading } = useGetCustomerDetailsForUtilityAdminQuery(customerId ?? "", {
        skip: !isOpen || !customerId,
    });
    const [unlinkMeter, { isLoading: isUnlinking }] = useUnlinkMeterByUtilityAdminMutation();

    // Reset the selections whenever the Modal is reopened.
    useEffect(() => {
        if (!isOpen) {
            setSelectedMeterId(null);
            setSelectedReason(null);
        }
    }, [isOpen]);

    const meterOptions = useMemo<DropdownOption[]>(
        () =>
            (customer?.meters ?? []).map(meter => {
                const number = meter.meterNumber || meter.accountNumber;
                const name = meter.name?.trim();
                const suffix = number ? ` - ${number}` : "";
                const label = name ? (
                    `${name}${suffix}`
                ) : (
                    <>
                        <span className={styles.missingName}>Name Unavailable</span>
                        {suffix}
                    </>
                );
                return { value: meter.id, label };
            }),
        [customer?.meters]
    );

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleUnlink = async () => {
        if (!customerId || !selectedMeterId || !selectedReason) return;

        try {
            await unlinkMeter({
                customerId,
                meterId: selectedMeterId,
                reason: Number(selectedReason),
            }).unwrap();
            toast.success("Meter unlinked successfully");
            onClose();
        } catch (e: any) {
            toast.error(getErrorMessage(e?.data));
        }
    };

    const canSubmit = !!selectedMeterId && !!selectedReason && !isUnlinking;

    return (
        <div className={styles.backdrop} onClick={handleBackdropClick}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Unlink Meter Number</h2>
                    <span className={styles.subTitle}>This action will permanently delete this meter from the customer's account</span>
                </div>
                <div className={styles.body}>
                    <div className={styles.field}>
                        <label className={styles.label}>Meter</label>
                        <Dropdown
                            options={meterOptions}
                            value={selectedMeterId}
                            onChange={setSelectedMeterId}
                            placeholder={isLoading ? "Loading meters..." : "Select a meter"}
                            disabled={isLoading || meterOptions.length === 0}
                            emptyText="No meters linked to this customer"
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>Reason for unlinking</label>
                        <Dropdown
                            options={UNLINK_REASONS}
                            value={selectedReason}
                            onChange={setSelectedReason}
                            placeholder="Select a reason"
                        />
                    </div>
                </div>
                <div className={styles.footer}>
                    <Button variant="outline" onClick={onClose} text="Cancel" disabled={isUnlinking} />
                    <Button variant="danger" onClick={handleUnlink} text={isUnlinking ? "Unlinking..." : "Unlink Meter"} disabled={!canSubmit} />
                </div>
            </div>
        </div>
    );
};

export default UnlinkMeterModal;
